import type { LineageDog, SiteContent } from './types';
import { SUGARLOAF_FACEBOOK_PAGE_URL } from './facebook';

export const sugarloafPlaceholderDogs: LineageDog[] = [
  {
    id: 'feather',
    name: 'Feather',
    registeredName: '',
    title: 'Profile details pending',
    image: { src: '/images/about.jpg', alt: 'Placeholder image for Feather’s profile' },
    description: 'Add Feather’s verified profile details and story here before publishing.',
    health: [],
    awards: [],
  },
  {
    id: 'matisse',
    name: 'Matisse',
    registeredName: '',
    title: 'Profile details pending',
    image: { src: '/images/hero_new.jpg', alt: 'Placeholder image for Matisse’s profile' },
    description: 'Add Matisse’s verified profile details and story here before publishing.',
    health: [],
    awards: [],
  },
  {
    id: 'venice',
    name: 'Venice',
    registeredName: '',
    title: 'Profile details pending',
    image: { src: '/images/bento_puppies.jpg', alt: 'Placeholder image for Venice’s profile' },
    description: 'Add Venice’s verified profile details and story here before publishing.',
    health: [],
    awards: [],
  },
  {
    id: 'ruger',
    name: 'Ruger',
    registeredName: '',
    title: 'Profile details pending',
    image: { src: '/images/lineage_portrait.jpg', alt: 'Placeholder image for Ruger’s profile' },
    description: 'Add Ruger’s verified profile details and story here before publishing.',
    health: [],
    awards: [],
  },
];

export const defaultSiteContent: SiteContent = {
  theme: {
    colors: {
      ink: '#3a332a',
      cream: '#fdfaf4',
      sand: '#f4edde',
      orange: '#de9445',
      muted: '#6e6455',
      line: '#e7dfcc',
    },
    typography: {
      heading: 'bricolage',
      body: 'nunito',
    },
    layout: {
      contentWidth: 1240,
      sectionSpacing: 48,
      cornerRadius: 24,
    },
    buttons: {
      shape: 'pill',
      tone: 'ink',
    },
  },
  sections: {
    order: ['hero', 'dogs', 'health', 'weeks', 'gallery', 'process', 'stories', 'quiz', 'litters', 'faq', 'facebook', 'contact', 'footer'],
    hidden: [],
  },
  features: {
    availabilityCard: true,
    trustBar: true,
    newsletterSignup: true,
  },
  heroPhotos: [
    '/images/bento_puppies.jpg',
    '/images/about.jpg',
    '/images/hero_mountain.jpg',
    '/images/lineage_portrait.jpg',
    '/images/hero_new.jpg',
    '/images/sleepy_golden.png',
    '/images/husky_puppy.png',
    '/images/grey_puppy.png',
  ],
  heroPhotoCrops: Array.from({ length: 8 }, () => null),
  gallery: [
    { src: '/images/hero_mountain.jpg', caption: 'puppy pile, week 2', tag: 'Puppies', span: 2 },
    { src: '/images/lineage_portrait.jpg', caption: 'first swim in the pond', tag: 'Adventures', span: 1 },
    { src: '/images/hero_new.jpg', caption: 'Vixen on the trail', tag: 'Adults', span: 1 },
    { src: '/images/sleepy_golden.png', caption: 'whelping box, day 1', tag: 'Puppies', span: 1 },
    { src: '/images/husky_puppy.png', caption: 'gotcha day hugs', tag: 'Puppies', span: 2 },
    { src: '/images/grey_puppy.png', caption: 'Apollo, autumn portrait', tag: 'Adults', span: 1 },
    { src: '/images/bento_puppies.jpg', caption: 'mountain overlook hike', tag: 'Adventures', span: 1 },
    { src: '/images/about.jpg', caption: 'nap on the porch', tag: 'Adults', span: 1 },
    { src: '/images/hero_mountain.jpg', caption: '6-week zoomies', tag: 'Puppies', span: 1 },
    { src: '/images/lineage_portrait.jpg', caption: 'creek day', tag: 'Adventures', span: 1 },
  ],
  facebook: {
    enabled: true,
    pageUrl: SUGARLOAF_FACEBOOK_PAGE_URL,
    eyebrow: 'FROM FACEBOOK',
    title: 'Life with the Sugarloaf pack',
    intro: 'Follow along for everyday moments, puppy updates, and news from Sugarloaf Golden Retrievers.',
    items: [],
  },
  hero: {
    eyebrow: 'Sugarloaf Goldens',
    title: 'Raised with intention, placed with care.',
    subtitle: 'Champion-bloodline goldens from Urbana, Maryland. Small litters, full health testing, lifetime support.',
    image: {
      src: '/images/hero_new.jpg',
      alt: 'Golden retriever standing on Sugarloaf Mountain at sunset overlooking Frederick County, Maryland',
    },
    ctaPrimary: 'Apply for a puppy',
    ctaSecondary: 'Meet the dogs',
    trust: ['OFA HEALTH-TESTED', 'GRCA CODE OF ETHICS', 'AKC BRED WITH H.E.A.R.T.', 'URBANA, MD'],
  },
  process: [
    { step: '1', title: 'Apply', body: "Ten minutes about your family, home, and what you're looking for in a golden. Drafts save automatically." },
    { step: '2', title: 'Meet us', body: 'A call, and a visit when possible, so you can meet our dogs and we can get to know each other.' },
    { step: '3', title: 'Get matched', body: 'At 7 weeks we temperament-test each puppy and match them to families, not the other way around.' },
    { step: '4', title: 'Gotcha day', body: 'Home at 8+ weeks with health records, a puppy pack, and our phone number for life.' },
  ],
  weeks: [
    { week: 'WEEKS 0–2', title: 'Neonatal & ENS', body: 'Early Neurological Stimulation daily from day 3, weight checks twice a day, and around-the-clock monitoring.' },
    { week: 'WEEK 3', title: 'Senses awaken', body: 'Eyes and ears open. We introduce gentle sounds, new textures underfoot, and their first taste of the wider world.' },
    { week: 'WEEKS 4–5', title: 'Play & manners', body: 'Littermates teach bite inhibition through play. Startle-and-recover exercises build bounce-back confidence.' },
    { week: 'WEEK 6', title: 'Crates & grooming', body: 'Crate conditioning begins, plus nail trims, brush time, and car rides — the scary firsts happen here, not at your house.' },
    { week: 'WEEK 7', title: 'Temperament testing', body: "Structured evaluation of each puppy's confidence, drive, and sensitivity — the data behind our family matching." },
    { week: 'WEEK 8', title: 'Gotcha day', body: 'Vet-checked and ready. You take home a confident puppy and our phone number for life.' },
  ],
  testimonials: [
    { quote: 'We visited three breeders before Sugarloaf. Seeing the dogs live in the house, the health clearances laid out on the table, it was a different level of care entirely.', name: 'Sarah & Mike', location: 'Arlington, VA' },
    { quote: "Our boy came home crate-conditioned and confident, our trainer couldn't believe he was 9 weeks old.", name: 'The Hendersons', location: 'Bethesda, MD' },
    { quote: 'Two years later and they still answer every question within a day. Worth every mile of the drive.', name: 'The Okafors', location: 'Philadelphia, PA' },
    { quote: 'Three years in and Bear still gets compliments on his manners daily. The temperament matching Sugarloaf did was spot on, exactly the calm, easygoing dog our family needed.', name: 'The Millers', location: 'Frederick, MD' },
    { quote: 'The weekly updates and photos meant we felt like part of the litter before we even brought her home.', name: 'Priya & Dev', location: 'Rockville, MD' },
    { quote: 'Health testing paperwork was ready before we even asked. That told us everything we needed to know.', name: 'The Caldwells', location: 'Leesburg, VA' },
  ],
  copy: {
    dogsKicker: 'THE FOUNDATION',
    dogsTitle: 'Meet our dogs',
    dogsSubtext: 'Every sire and dam is selected for health, temperament, and structure — and lives here as family first. Tap a card for pedigree and clearances.',
    healthKicker: '',
    healthTitle: 'Health before everything.',
    healthIntro: 'Every pairing is fully health-tested to the Golden Retriever Club of America Code of Ethics — before a single puppy is planned. Ask us for clearances anytime; we share them proudly.',
    weeksKicker: "BEFORE THEY'RE YOURS",
    weeksTitle: 'The first eight weeks',
    weeksSubtext: "Your puppy's foundation is built long before gotcha day. Every Sugarloaf litter follows this curriculum.",
    galleryKicker: 'GOLDEN MOMENTS',
    galleryTitle: 'Straight from the whelping box',
    processKicker: 'HOW IT WORKS',
    processTitle: 'From hello to gotcha day',
    processSubtext: 'Four steps. No mystery, no pressure.',
    testimonialsKicker: 'FROM OUR FAMILIES',
    testimonialsTitle: '60+ placements since 2019',
    quizKicker: 'READINESS CHECK',
    quizTitle: 'Is a golden right for you?',
    quizSubtext: 'Five honest questions about time, budget, and shedding tolerance. We’d rather you find out now than eight weeks in — an honest “not yet” is a fine answer.',
    littersKicker: 'CURRENT AVAILABILITY',
    littersTitle: 'Upcoming litters',
    littersSubtext: 'A small number of puppies, placed with intention',
    faqKicker: 'GOOD QUESTIONS',
    faqTitle: 'Frequently asked',
    contactKicker: 'ALWAYS HAPPY TO CHAT',
    contactTitle: 'Get in touch',
    contactIntro: 'Questions about our dogs, our breeding practices, or an upcoming litter? Send a message — we answer everything, usually within a day.',
    contactEmail: 'sugarloafgoldens@gmail.com',
    contactInstagram: '@sugarloafgoldens',
    footerBlurb: 'Urbana, Maryland — in the shadow of Sugarloaf Mountain. Be the first to hear about upcoming litters. No spam, just puppies.',
  },
  heritage: {
    eyebrow: 'The Sugarloaf Legacy',
    title: 'Maryland Roots,',
    emphasis: 'Golden Hearts.',
    body: 'Since 2019, Sugarloaf Goldens has been a cornerstone of the Urbana and Frederick County community. Our breeding program is inspired by the rugged beauty of Sugarloaf Mountain and the warmth of Maryland hospitality.',
    image: { src: '/images/about.jpg', alt: 'Sugarloaf Goldens puppy resting on a cozy knit blanket' },
    quote: "The mountain's silhouette is an early reminder of the strength and beauty we breed into our Goldens.",
    tags: ['Est. 2019', 'Family Raised', 'Urbana, MD', 'GRCA Member', 'AKC Bred with H.E.A.R.T.'],
    stats: [
      { value: 7, label: 'Years Breeding' },
      { value: 12, label: 'Litters Raised' },
      { value: 9, label: 'Champion Titles' },
      { value: 60, suffix: '+', label: 'Happy Families' },
    ],
  },
  healthStandards: [
    { code: 'OFA', title: 'Hips & elbows', body: 'OFA evaluations on every sire and dam to protect joint health for a lifetime of trail walks.' },
    { code: '', title: 'Cardiac', body: 'Heart clearances performed by a board-certified veterinary cardiologist.' },
    { code: '', title: 'Eyes', body: 'Annual eye examinations by a veterinary ophthalmologist.' },
    { code: 'DNA', title: 'Genetics', body: 'Full panels screen for inheritable conditions so we never double up on carriers.' },
  ],
  lineage: [
    {
      id: 'vixen',
      name: 'Vixen',
      registeredName: 'Charms Vixen with an Attitude',
      title: 'AKC Grand Champion',
      image: { src: '/images/lineage_portrait.jpg', alt: 'Vixen — AKC Grand Champion golden retriever portrait' },
      description: 'Vixen is the heart of Sugarloaf. Her classic structure, eager temperament, and deeply affectionate nature set the standard for our program.',
      health: ['OFA Hips Good', 'OFA Elbows Normal', 'Cardiac Clear', 'CAER Clear', 'DNA Clear'],
      awards: ['AKC Grand Champion', 'Best of Breed Winner'],
      pedigree: {
        sire: 'GCH Charms River of Gold',
        sireGrands: ['CH Goldrush Storybook', 'Charms Evening Star'],
        dam: 'Charms Summer Song',
        damGrands: ['GCH Seabreeze Captain', 'Charms Golden Hour'],
      },
    },
    {
      id: 'fame',
      name: 'Fame',
      registeredName: 'Firestars Save the Best for Last',
      title: 'Champion',
      image: { src: '/images/lineage_portrait.jpg', alt: 'Fame — Champion golden retriever portrait' },
      description: 'Fame combines an effortless show-ring presence with the steady, people-focused temperament we prize at home.',
      health: ['OFA Hips Good', 'OFA Elbows Normal', 'Cardiac Clear', 'CAER Clear'],
      awards: ['AKC Champion'],
      filter: 'sepia(0.4) grayscale(0.5) contrast(1.1)',
    },
    {
      id: 'apollo',
      name: 'Apollo',
      registeredName: "Vixena's Fly Me to The Moon",
      title: 'Champion',
      image: { src: '/images/lineage_portrait.jpg', alt: 'Apollo — Champion golden retriever portrait' },
      description: 'Apollo brings balanced movement, beautiful type, and a joyful working attitude to the Sugarloaf family.',
      health: ['OFA Hips Excellent', 'OFA Elbows Normal', 'Cardiac Clear', 'CAER Clear'],
      awards: ['AKC Champion'],
      filter: 'sepia(0.2) grayscale(0.8) contrast(1.2)',
      pedigree: {
        sire: 'CH Vixenas Man in the Moon',
        sireGrands: ['GCH Moonlights High Tide', 'CH Vixenas First Edition'],
        dam: 'Vixenas Stardust',
        damGrands: ['CH Goldcrest Night Sky', 'Vixenas Silver Lining'],
      },
    },
  ],
  faq: [
    { question: 'How does your waitlist work?', answer: 'Start with the application. Approved families are invited to join the waitlist for an upcoming litter with a deposit. We keep our lists short so no one waits indefinitely.' },
    { question: 'What health testing do you do?', answer: 'All of our sires and dams have OFA hip and elbow evaluations, cardiac clearances by a board-certified cardiologist, annual CAER eye exams, and DNA panels — the full Golden Retriever Club of America recommendation.' },
    { question: 'What does a puppy cost?', answer: 'Our puppies are priced in line with fully health-tested, champion-bloodline programs in the mid-Atlantic. We share exact pricing and deposit details with approved applicants.' },
    { question: 'Do you ship puppies?', answer: "We don't cargo-ship. Families within driving distance pick up in person, and for farther homes we can discuss in-cabin flight nanny options case by case." },
    { question: 'Can I choose my puppy?', answer: 'We guide placements using temperament observations, family needs, and preference. The goal is the best lifelong match, not simply the earliest pick.' },
    { question: 'How are puppies socialized?', answer: 'Puppies are raised in our home with age-appropriate handling, household sounds, novel surfaces, visitors, crates, car rides, and early foundations for training.' },
    { question: 'What support do you provide after pickup?', answer: 'Every Sugarloaf family receives lifetime breeder support. We are available for training, health, nutrition, and transition questions throughout your dog’s life.' },
  ],
};
